import { getStrokeWidthByKey } from "@excalidraw/common";
import { pointFrom } from "@excalidraw/math";

import type { GlobalPoint, LocalPoint } from "@excalidraw/math";

import type { AppState } from "@excalidraw/excalidraw/types";

import { LinearElementEditor } from "../linearElementEditor";
import { newLinearElement } from "../newElement";

import type { Scene } from "../Scene";

import type { PenPathElement, PenPointHandles } from "./types";

/**
 * State machine for interactively drawing a Penpot-style bezier pen path.
 *
 * All scene writes go through `scene.mutateElement`; the functions are otherwise
 * pure (take a session, return the next session). `handles` is kept parallel to
 * `points` at every step and `points[0]` is always kept at (0,0).
 */
export type PenSession = {
  elementId: string;
  /** index of the anchor whose handles a drag is currently shaping, or null */
  draggingAnchor: number | null;
  /** whether a trailing uncommitted rubber-band point is present */
  hasPreview: boolean;
};

/** snaps a vector to the nearest 45°, preserving its length */
export const snap45 = (dx: number, dy: number): [number, number] => {
  const dist = Math.hypot(dx, dy);
  const angle = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4);
  return [Math.cos(angle) * dist, Math.sin(angle) * dist];
};

const clonePoints = (points: readonly LocalPoint[]): LocalPoint[] =>
  points.map((p) => pointFrom<LocalPoint>(p[0], p[1]));

export const startPenPath = (
  coords: { x: number; y: number },
  appState: AppState,
  scene: Scene,
): { session: PenSession; element: PenPathElement } => {
  const element = newLinearElement({
    type: "line",
    x: coords.x,
    y: coords.y,
    points: [pointFrom<LocalPoint>(0, 0)],
    handles: [null],
    strokeColor: appState.currentItemStrokeColor,
    backgroundColor: appState.currentItemBackgroundColor,
    fillStyle: appState.currentItemFillStyle,
    strokeWidth: getStrokeWidthByKey(
      "line",
      appState.currentItemStrokeWidthKey,
    ),
    strokeStyle: appState.currentItemStrokeStyle,
    roughness: appState.currentItemRoughness,
    opacity: appState.currentItemOpacity,
    // bezier handles do the curving; element roundness would double-smooth
    roundness: null,
    locked: false,
  }) as unknown as PenPathElement;

  scene.insertElement(element);

  return {
    session: { elementId: element.id, draggingAnchor: 0, hasPreview: false },
    element,
  };
};

/**
 * Draw-nodes sub-mode: resumes a pen session appending from an existing open
 * path's endpoint. `appendAnchor` always extends the tail (`points[length -
 * 1]`), so clicking the start (index 0) reverses `points`/`handles` first —
 * re-normalizing so the new points[0] stays at local (0,0) — and swaps each
 * handle's `in`/`out`, since reversing the traversal direction swaps which
 * side of the anchor now points "backward" vs "forward".
 */
export const resumePenPath = (
  element: PenPathElement,
  endpointIndex: number,
  scene: Scene,
): PenSession => {
  if (endpointIndex === 0 && element.points.length > 1) {
    const reversedPoints = clonePoints(element.points).reverse();
    const reversedHandles = [...element.handles].reverse().map(
      (handle): PenPointHandles =>
        handle
          ? {
              ...(handle.out ? { in: handle.out } : {}),
              ...(handle.in ? { out: handle.in } : {}),
            }
          : null,
    );
    const normalized = LinearElementEditor.getNormalizeElementPointsAndCoords({
      ...element,
      points: reversedPoints,
    });
    scene.mutateElement(element, {
      points: normalized.points,
      handles: reversedHandles,
      x: normalized.x,
      y: normalized.y,
    });
  }

  return { elementId: element.id, draggingAnchor: null, hasPreview: false };
};

/** index of the last committed anchor, ignoring a trailing preview point */
const lastCommittedIndex = (
  element: PenPathElement,
  session: PenSession,
): number => element.points.length - 1 - (session.hasPreview ? 1 : 0);

export const appendAnchor = (
  session: PenSession,
  element: PenPathElement,
  scene: Scene,
  local: LocalPoint,
  opts: { shift: boolean },
): PenSession => {
  const points = clonePoints(element.points);
  const handles = [...element.handles];

  let anchor = local;
  if (opts.shift) {
    const prev = points[lastCommittedIndex(element, session)];
    const [dx, dy] = snap45(local[0] - prev[0], local[1] - prev[1]);
    anchor = pointFrom<LocalPoint>(prev[0] + dx, prev[1] + dy);
  }

  let index: number;
  if (session.hasPreview) {
    // reuse the trailing preview point as the committed anchor (no splice churn)
    index = points.length - 1;
    points[index] = anchor;
    handles[index] = null;
  } else {
    points.push(anchor);
    handles.push(null);
    index = points.length - 1;
  }

  scene.mutateElement(element, { points, handles });

  return { ...session, draggingAnchor: index, hasPreview: false };
};

export const dragHandles = (
  session: PenSession,
  element: PenPathElement,
  scene: Scene,
  local: LocalPoint,
  opts: { shift: boolean; alt?: boolean },
): void => {
  if (session.draggingAnchor === null) {
    return;
  }
  const i = session.draggingAnchor;
  const anchor = element.points[i];
  let dx = local[0] - anchor[0];
  let dy = local[1] - anchor[1];
  if (opts.shift) {
    [dx, dy] = snap45(dx, dy);
  }
  const handles = [...element.handles];
  // alt breaks the mirror (Figma): out follows the cursor, in stays put
  const prevIn = handles[i]?.in;
  handles[i] = {
    out: pointFrom<LocalPoint>(dx, dy),
    in: opts.alt && prevIn ? prevIn : pointFrom<LocalPoint>(-dx, -dy),
  };
  scene.mutateElement(element, { handles });
};

export const commitAnchor = (session: PenSession): PenSession => ({
  ...session,
  draggingAnchor: null,
});

// maintains a trailing "preview" point at the cursor so a rubber-band segment
// extends from the last committed anchor; appendAnchor reuses it and
// finish/close drop it via withoutPreview.
export const updatePreview = (
  session: PenSession,
  element: PenPathElement,
  scene: Scene,
  local: LocalPoint,
  opts: { shift: boolean },
): PenSession => {
  const points = clonePoints(element.points);
  const handles = [...element.handles];

  let preview = local;
  if (opts.shift) {
    const prev = points[lastCommittedIndex(element, session)];
    const [dx, dy] = snap45(local[0] - prev[0], local[1] - prev[1]);
    preview = pointFrom<LocalPoint>(prev[0] + dx, prev[1] + dy);
  }

  if (session.hasPreview) {
    points[points.length - 1] = preview;
  } else {
    points.push(preview);
    handles.push(null);
  }
  scene.mutateElement(element, { points, handles });

  return { ...session, hasPreview: true };
};

export const hitsFirstAnchor = (
  element: PenPathElement,
  session: PenSession,
  scenePointer: GlobalPoint,
  zoomValue: number,
): boolean => {
  // exclude the trailing preview point from the committed-anchor count
  const committed = element.points.length - (session.hasPreview ? 1 : 0);
  if (committed < 3) {
    return false;
  }
  const threshold = LinearElementEditor.POINT_HANDLE_SIZE / zoomValue;
  const first = element.points[0];
  return (
    Math.hypot(
      scenePointer[0] - (element.x + first[0]),
      scenePointer[1] - (element.y + first[1]),
    ) <= threshold
  );
};

const withoutPreview = (
  element: PenPathElement,
  session: PenSession,
): { points: LocalPoint[]; handles: PenPointHandles[] } => {
  const drop = session.hasPreview ? -1 : undefined;
  return {
    points: clonePoints(drop ? element.points.slice(0, drop) : element.points),
    handles: [...(drop ? element.handles.slice(0, drop) : element.handles)],
  };
};

export const closePenPath = (
  session: PenSession,
  element: PenPathElement,
  scene: Scene,
): void => {
  const { points, handles } = withoutPreview(element, session);
  // duplicate anchor 0 at the seam and mark the ring closed (mirrors joinEndpoints)
  points.push(pointFrom<LocalPoint>(points[0][0], points[0][1]));
  handles.push(null);
  scene.mutateElement(element, { points, handles, polygon: true });
};

export const finishPenPath = (
  session: PenSession,
  element: PenPathElement,
  scene: Scene,
): "kept" | "discarded" => {
  const { points, handles } = withoutPreview(element, session);

  if (points.length < 2) {
    scene.mutateElement(element, { isDeleted: true });
    return "discarded";
  }

  const normalized = LinearElementEditor.getNormalizeElementPointsAndCoords({
    ...element,
    points,
  });
  scene.mutateElement(element, {
    points: normalized.points,
    handles,
    x: normalized.x,
    y: normalized.y,
  });
  return "kept";
};
